/* Configuration versions and the `/cockpit/v1` reads → the admin screens.
 *
 * Two different kinds of mapping live here, and it is worth saying which is
 * which.
 *
 * The configuration slice is TMF-shaped and generic: a `BusinessRuleVersion` is
 * a rule key, a class, a declarative expression and a severity. The admin
 * screens are specific: they show a sentence, a scope and a version. Turning the
 * first into the second is interpretation, and every place this file interprets,
 * it says so and falls back to something the reader can act on — a reason code
 * rather than an invented sentence.
 *
 * The `/cockpit/v1` reads are the opposite. They were designed against these
 * screens, so `settings` and `cohort` arrive in almost exactly the shape the
 * screens want. What is left here is narrowing: the wire types those endpoints
 * publish are open strings where the cockpit has unions, and this is where the
 * two are reconciled rather than asserted with a cast.
 */

import type { components } from '@/shared/api/generated/openapi';
import { DASH, formatDate } from '@/shared/api/http/map/format';
import type { CohortMeasurements, MriCompletion, PurgeReport } from '@/shared/domain/analytics';
import type {
  BusinessRule,
  ClarificationTemplate,
  ClassificationRule,
  Threshold,
  ThresholdKey,
} from '@/shared/domain/configuration';
import type { Characteristic } from '@/shared/domain/serviceCatalog';
import type { ClarificationPolicy, CockpitSettings, Integration } from '@/shared/domain/settings';
import type { StatusDisposition } from '@/shared/domain/status';

export type WireBusinessRule = components['schemas']['BusinessRuleModel'];
export type WireThreshold = components['schemas']['RoutingThresholdModel'];
export type WireTemplate = components['schemas']['ClarificationTemplateModel'];
export type WireSettings = components['schemas']['CockpitSettingsModel'];
export type WireIntegration = components['schemas']['IntegrationModel'];
export type WirePolicy = components['schemas']['ClarificationPolicyModel'];
export type WireCohort = components['schemas']['CohortModel'];
export type WirePurgeReport = components['schemas']['PurgeReportModel'];

/** The wire's raw tone word → the semantic disposition it stands for.
 *
 * `IntegrationModel.tone` is a free string that happens to carry the cockpit's
 * old presentation vocabulary verbatim — a quirk of the wire contract, not a
 * business fact this side should keep. Translating it here, once, means
 * `Integration.disposition` is a semantic category like any other domain field
 * and nothing downstream has to know the wire ever spoke in tones. */
const WIRE_TONE_TO_DISPOSITION: Readonly<Record<string, StatusDisposition>> = {
  neutral: 'neutral',
  ai: 'automation',
  clar: 'clarification',
  conf: 'conflict',
  manual: 'manual',
  ready: 'ready',
  signed: 'completed',
  unsafe: 'blocked',
};

function titled(value: string | null | undefined): string {
  if (!value) return DASH;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatFloor(floor: number | string | null | undefined): string {
  if (floor === null || floor === undefined || floor === '') return DASH;
  const value = typeof floor === 'number' ? floor : Number(floor);
  return Number.isNaN(value) ? DASH : value.toFixed(2);
}

/** A string field of a rule's declarative expression, if it holds one.
 *
 * The expression is a document evaluated by the interpreter named in
 * `expressionLanguage`; its shape is the rule DSL's business, not this file's.
 * Reading one key defensively is the whole of the coupling — a rule written in a
 * dialect that does not use these keys renders from its reason code instead of
 * failing to render. */
function phrase(expression: Record<string, unknown>, ...keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = expression[key];
    if (typeof value === 'string' && value !== '') return value;
  }
  return null;
}

/* ---------- rules ---------- */

/** How a rule reads when its expression carries no prose.
 *
 * The reason code is the sentence's subject — `BR-FIN-001` fails with
 * `cost-centre-invalid`, and that is what an operator will see quoted on a case.
 * Naming the characteristic beside it turns a code into something locatable
 * without pretending to be the English the rule author never wrote. */
function fallbackStatement(wire: WireBusinessRule): string {
  return wire.appliesToCharacteristic
    ? `${wire.failureReasonCode} — ${wire.appliesToCharacteristic}`
    : wire.failureReasonCode;
}

/**
 * Validation and gate rules → the business-rules table.
 *
 * Classification rules are excluded: they answer a different question — what
 * kind of order this is, rather than whether it may proceed — and the admin
 * screens separate them for that reason. `toClassificationRules` takes those.
 *
 * `version` is the configuration version number, not a release. These rows are
 * frozen on activation, so `v11` names an exact document that can be fetched and
 * compared, which is what makes "which rules was this case judged under" a
 * question with an answer.
 */
export function toBusinessRules(wires: readonly WireBusinessRule[]): readonly BusinessRule[] {
  return wires
    .filter((wire) => wire.ruleClass !== 'classification')
    .map((wire) => ({
      id: wire.ruleKey,
      scope: wire.appliesToCharacteristic ?? titled(wire.ruleClass),
      statement: phrase(wire.expression, 'statement', 'description') ?? fallbackStatement(wire),
      // The API derives `blocking` from the severity vocabulary; the cockpit has
      // two words for it. Anything not blocking is a warning — recorded, shown,
      // and not in the way.
      severity: wire.blocking ? ('Blocking' as const) : ('Warning' as const),
      version: `v${wire.versionNo}`,
    }));
}

/**
 * Classification rules → the when/then table.
 *
 * `then` is assembled from the order action and category where the expression
 * names them, because that pair *is* the classification — `add · Provide` is
 * what the rule asserts, and it is the same vocabulary the case row carries.
 *
 * `floor` is the rule's own confidence floor, below which it asserts nothing.
 * A rule with none shows a dash: no floor means the rule always asserts, and
 * printing `0.00` would say the same thing far less clearly.
 */
export function toClassificationRules(
  wires: readonly WireBusinessRule[],
): readonly ClassificationRule[] {
  return wires
    .filter((wire) => wire.ruleClass === 'classification')
    .map((wire) => {
      const action = phrase(wire.expression, 'orderAction', 'action');
      const category = phrase(wire.expression, 'orderCategory', 'category');
      const classified = action && category ? `${action} · ${category}` : null;

      return {
        id: wire.ruleKey,
        when: phrase(wire.expression, 'when', 'if', 'match') ?? fallbackStatement(wire),
        then: phrase(wire.expression, 'then', 'classify', 'result') ?? classified ?? DASH,
        floor: formatFloor(wire.confidenceFloor),
        // Who activated the version in force. Not a maintained owner field —
        // there is no such column — but it is a real name attached to a real
        // decision, which is what the column is being read for.
        owner: wire.activatedBy ?? DASH,
      };
    });
}

/* ---------- thresholds ---------- */

/** The four confidence lines a threshold version holds, in reading order.
 *
 * One version row carries four numbers; the settings screen shows four lines.
 * The numbers, the owner and the lock all come from the version.
 *
 * What each line is *called*, and what crossing it does, is not here. Those are
 * the cockpit's own sentences — no column holds them — and a sentence assembled
 * at fetch time would sit in React Query's cache in the language it was fetched
 * in. So this table carries the identifier and the accessor, and the identifier
 * is the catalogue key the settings screen reads the wording from.
 *
 * `TH-SAFETY` is security-owned and the cockpit renders it locked. That is not a
 * convention this file invents: a customer-scoped threshold row cannot carry a
 * security owner at all, enforced by a CHECK, and `locked` on the wire is the
 * readable restatement of it.
 */
const THRESHOLD_LINES = [
  { id: 'TH-READY', of: (wire: WireThreshold) => wire.readyThreshold },
  { id: 'TH-CLASS', of: (wire: WireThreshold) => wire.classificationFloor },
  { id: 'TH-FIELD', of: (wire: WireThreshold) => wire.fieldConfidenceDefault },
  { id: 'TH-SAFETY', of: (wire: WireThreshold) => wire.safetyFloor },
] as const satisfies readonly {
  readonly id: ThresholdKey;
  readonly of: (wire: WireThreshold) => unknown;
}[];

/**
 * The active threshold version → the four lines the settings screen shows.
 *
 * Takes the version rather than a list, because only one is in force at a time
 * and a screen showing two sets of floors would be showing one that is not being
 * applied. No active version yields no rows, and the screen says so — which is
 * true and is a configuration error worth surfacing.
 */
export function toThresholds(wire: WireThreshold | null | undefined): readonly Threshold[] {
  if (!wire) return [];
  return THRESHOLD_LINES.map((line) => ({
    id: line.id,
    value: formatFloor(line.of(wire)),
    // Security owns the safety floor by constraint; the desk owns the rest.
    owner:
      line.id === 'TH-SAFETY' || wire.owner === 'security'
        ? ('Security' as const)
        : ('Business' as const),
    ...(line.id === 'TH-SAFETY' || wire.locked ? { locked: true } : {}),
  }));
}

/* ---------- templates ---------- */

const DIMENSIONS: readonly Characteristic['dimension'][] = [
  'Who',
  'What',
  'Why',
  'Where',
  'When',
  'How',
];

/** The register keys `composeClarification` knows. Restated here rather than
    imported, because importing them would make the wording layer a dependency of
    the transport — and this file's job is to hand that layer a key it
    recognises, not to know what the key renders as. */
const REGISTERS: ReadonlySet<string> = new Set(['EN-formal', 'EN-neutral', 'DE-formal']);

/** Languages with exactly one register, and only those. English is deliberately
    absent: it has two and nothing stored chooses between them. */
const SOLE_REGISTER: ReadonlyMap<string, string> = new Map([['de', 'DE-formal']]);

/**
 * A stored BCP 47 tag → the register the composer names its wording by.
 *
 * These are two vocabularies for two different things. The API stores `de`,
 * because a template version's language is part of its uniqueness scope and
 * BCP 47 is what a language is. The cockpit's registers are keyed
 * `LANG-formality`, because a register is a language *and* a way of addressing
 * someone. `composeClarification` looks up the second by the first, and an
 * unrecognised key falls to the neutral register — which is English. Passing a
 * BCP 47 tag through unresolved therefore renders a German template's greeting,
 * opening and section titles in English, silently, on the screen where a
 * reviewer reads the letter before approving it.
 *
 * The rule is: resolve a language to a register only where the language has one
 * register. German does — `DE-formal` is the only German wording that exists, so
 * naming it asserts nothing about formality that the register set has not
 * already settled. English has two, and nothing stored says which a template was
 * written in, so English resolves to nothing and the composer's own neutral
 * fallback stands where it belongs.
 *
 * A value that is already a register key is returned untouched, so the fixtures
 * — which carry `DE-formal` directly — pass through unchanged and the two sides
 * of the seam compose the same letter.
 */
export function toRegister(language: string): string {
  if (REGISTERS.has(language)) return language;
  // `de-DE` and `de-AT` are the same wording; the primary subtag is what decides.
  const primary = language.toLowerCase().split('-')[0] ?? '';
  return SOLE_REGISTER.get(primary) ?? language;
}

/**
 * Clarification template versions → the wording-layer table.
 *
 * `customerNames` resolves the tenant id to the name the column shows. A
 * global-scope template has no tenant, so it has no name to resolve — the
 * column reads "All customers" for it, written at render because that is a
 * cockpit word rather than a fact from the wire.
 *
 * Three fields have no backing column and are reported as what the system
 * actually does rather than left out:
 *
 *   `sections`          all six dimensions. The letter asks for whatever the
 *                       case is missing, and the gap list is a placeholder — so
 *                       no dimension is dropped by the template.
 *   `overrides`         empty. Per-characteristic phrasing overrides are a
 *                       question-bank feature and nothing stores them yet.
 *   `suppressAnswered`  true. Questions are generated from open gaps, so an
 *                       answered field is never asked about; that is structural
 *                       rather than a setting somebody could turn off.
 *
 * Stating them this way keeps the coverage check on the admin screen honest: it
 * reports full coverage because coverage *is* full, not because the number was
 * typed in.
 */
export function toTemplates(
  wires: readonly WireTemplate[],
  customerNames: ReadonlyMap<string, string> = new Map(),
): readonly ClarificationTemplate[] {
  return wires.map((wire) => ({
    // Key and version together: `CLAR-AUR-EN-v6` names one frozen document, and
    // the customers screen quotes exactly this string.
    id: `${wire.templateKey}-v${wire.versionNo}`,
    // A name, or nothing — never the id, and never a phrase. The same rule the
    // case mapper follows, for the same reason: a row assembled here is cached
    // by React Query, so a word written here would keep the language it was
    // fetched in. `tenant` below says which of the two nothings this is, and
    // `vocabulary.templateCustomer()` writes the word at render.
    customer: wire.customerId ? (customerNames.get(wire.customerId) ?? null) : null,
    // The id itself, kept because it is a join key: `templateFor` resolves a
    // template by tenant so that a renamed customer keeps its wording. Nothing
    // renders it.
    tenant: wire.customerId ?? null,
    language: toRegister(wire.language),
    state: wire.status === 'active' ? ('Live' as const) : ('Draft' as const),
    updated: formatDate(wire.activatedAt),
    owner: wire.activatedBy ?? DASH,
    version: wire.versionNo,
    sections: DIMENSIONS,
    overrides: {},
    suppressAnswered: true,
  }));
}

/** Customer id → the template id in force for it, for the customers screen.
 *
 * Only the tenant-scoped templates. A customer covered by the global fallback
 * has had no decision taken for it, and naming the default template in that
 * column would report one that was never made. */
export function templatesByTenant(
  wires: readonly WireTemplate[],
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const wire of wires) {
    if (!wire.customerId || wire.status !== 'active') continue;
    index.set(wire.customerId, `${wire.templateKey}-v${wire.versionNo}`);
  }
  return index;
}

/* ---------- cockpit settings ---------- */

/** The integration's disposition, narrowed rather than cast.
 *
 * `StatusDisposition` decides how urgently an integration's state should read,
 * and an unrecognised wire tone falling through to `neutral` is the right
 * failure: a state the cockpit has no category for is treated plainly instead
 * of claiming a category that means something else. */
function dispositionOf(raw: string): StatusDisposition {
  return WIRE_TONE_TO_DISPOSITION[raw] ?? 'neutral';
}

function toIntegrations(wires: readonly WireIntegration[]): readonly Integration[] {
  return wires.map((wire) => ({
    system: wire.system,
    purpose: wire.purpose,
    state: wire.state,
    disposition: dispositionOf(wire.tone),
  }));
}

/** Who a clarification goes to, and whether a person must approve it first.
 *
 * `recipientMode` is a two-value union on the cockpit and an open string on the
 * wire. It is narrowed by comparison, and anything unrecognised resolves to
 * `opsMailbox` — the more conservative reading. Getting this wrong in the other
 * direction would tell an operator that mail goes straight to a customer when
 * the deployment routes it through an ops desk first. */
function toPolicy(wire: WirePolicy): ClarificationPolicy {
  return {
    requireHitlApproval: wire.requireHitlApproval,
    recipientMode:
      wire.recipientMode === 'requesterDirect'
        ? ('requesterDirect' as const)
        : ('opsMailbox' as const),
    includeOpsCc: wire.includeOpsCc,
    dispatchModeVaries: wire.dispatchModeVaries ?? false,
    opsCc: wire.opsCc ?? null,
    reminderAfterDays: wire.reminderAfterDays ?? null,
  };
}

/**
 * `/cockpit/v1/settings` → the settings screen, almost unchanged.
 *
 * This endpoint was designed against this screen, so the mapping is narrowing
 * rather than translation. `notificationsConfigurable` is passed straight
 * through and is the important field on the page: it is false while no table
 * stores a preference, and the screen reads it to decide whether the list is a
 * set of controls or a report. A toggle that silently does nothing is worse than
 * no toggle.
 */
export function toCockpitSettings(wire: WireSettings): CockpitSettings {
  return {
    notifications: (wire.notifications ?? []).map((preference) => ({
      id: preference.id,
      label: preference.label,
      on: preference.on,
      note: preference.note ?? '',
      ...(preference.locked ? { locked: true } : {}),
    })),
    notificationsConfigurable: wire.notificationsConfigurable ?? false,
    policyTenant: wire.policyTenant,
    clarification: toPolicy(wire.clarification),
    integrations: toIntegrations(wire.integrations ?? []),
    status: {
      platform: wire.status.platform,
      ingestionLag: wire.status.ingestionLag,
      analysisQueue: wire.status.analysisQueue,
      lastPurge: wire.status.lastPurge,
    },
  };
}

/* ---------- cohort ---------- */

/**
 * `/cockpit/v1/cohort` → the analytics measurements.
 *
 * A straight carry, and the nulls are the point. Six of the seven numbers are
 * nullable and each null means something specific: the two accuracy figures need
 * a human verdict on whether a reading was right and nothing records one, and
 * the rest are null when their denominator is empty. The screens render a dash.
 *
 * Nothing here substitutes a zero. A tenant with no routing decisions has no
 * override rate, and `0%` would read as "nobody ever overrides" — which is the
 * opposite of "we do not know", and is the kind of number that ends up in a
 * quarterly report.
 */
export function toCohort(wire: WireCohort): CohortMeasurements {
  // MRI completion breakdown — present only when the backend sends it.
  const mriWire = (wire as unknown as { mriCompletion?: Record<string, number> }).mriCompletion;
  const mriCompletion: MriCompletion | null = mriWire
    ? {
        totalReceived: mriWire['totalReceived'] ?? 0,
        totalSignedOff: mriWire['totalSignedOff'] ?? 0,
        totalTakeover: mriWire['totalTakeover'] ?? 0,
        totalMriExtracted: mriWire['totalMriExtracted'] ?? 0,
        signoffNoTakeover: mriWire['signoffNoTakeover'] ?? 0,
        humanSignoffAfterTakeover: mriWire['humanSignoffAfterTakeover'] ?? 0,
      }
    : null;

  // HITL override counts — present only when the backend sends them.
  const hitlCount: number =
    (wire as unknown as { hitlCount?: number }).hitlCount ?? 0;
  const hitlTotal: number =
    (wire as unknown as { hitlTotal?: number }).hitlTotal ?? 0;
  const hitlOverrideRate: number | null =
    (wire as unknown as { hitlOverrideRate?: number | null }).hitlOverrideRate ??
    (hitlTotal > 0 ? hitlCount / hitlTotal : null);

  return {
    classificationAccuracy: wire.classificationAccuracy,
    extractionAccuracy: wire.extractionAccuracy,
    clarificationAcceptance: wire.clarificationAcceptance,
    overrideRate: wire.overrideRate,
    falseReadyRate: wire.falseReadyRate,
    medianCycleHours: wire.medianCycleHours,
    sampleSize: wire.sampleSize,
    window: wire.window,
    weeklyIntake: wire.weeklyIntake ?? [],
    weekdayLabels: wire.weekdayLabels ?? [],
    hitlOverrideRate,
    hitlCount,
    hitlTotal,
    mriCompletion,
  };
}

/* ---------- purge report ---------- */

/**
 * `/cockpit/v1/purge-report` → the Reports purge card.
 *
 * A straight carry. `records` and `previousStates` are optional on the wire only
 * because they have defaults there; both are normalised to empty lists so no
 * screen asks whether "nothing purged" arrived as `[]` or as absent.
 */
export function toPurgeReport(wire: WirePurgeReport): PurgeReport {
  return {
    totalCases: wire.totalCases,
    totalCaseItems: wire.totalCaseItems,
    previousStates: (wire.previousStates ?? []).map((entry) => ({
      state: entry.state,
      cases: entry.cases,
    })),
    records: (wire.records ?? []).map((record) => ({
      id: record.id,
      purgedAt: record.purgedAt,
      previousState: record.previousState,
      retentionDays: record.retentionDays,
      caseItemCount: record.caseItemCount,
      parentEmailCount: record.parentEmailCount,
      outcome: record.outcome,
      verificationMethod: record.verificationMethod,
      subjectIdHash: record.subjectIdHash,
      verificationHash: record.verificationHash,
      createdBy: record.createdBy,
    })),
  };
}
